import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Input } from 'components/ui/input'
import { Switch } from 'components/ui/switch'
import { Badge } from 'components/ui/badge'
import { Trash2, Plus, Loader2, Database } from 'lucide-react'
import {
	fetchLookupLists,
	createLookupList,
	updateLookupList,
	deleteLookupList,
	seedLookupLists,
} from 'slices/lookupListSlice'

interface LookupListSectionProps {
	listType: string
	title: string
	items: any[]
	dispatch: any
}

const LookupListSection = ({ listType, title, items, dispatch }: LookupListSectionProps) => {
	const [newOption, setNewOption] = useState('')
	const [newIsPermanent, setNewIsPermanent] = useState(false)

	const handleAdd = () => {
		const trimmed = newOption.trim()
		if (!trimmed) return
		dispatch(createLookupList({ listType, option: trimmed, isPermanent: newIsPermanent }) as any)
		setNewOption('')
		setNewIsPermanent(false)
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			handleAdd()
		}
	}

	const handleTogglePermanent = (item: any) => {
		dispatch(updateLookupList({ _id: item._id, isPermanent: !item.isPermanent }) as any)
	}

	const handleDelete = (item: any) => {
		dispatch(deleteLookupList(item._id) as any)
	}

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
			<h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>

			{/* Add new option */}
			<div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
				<Input
					placeholder="New option..."
					value={newOption}
					onChange={(e: any) => setNewOption(e.target.value)}
					onKeyDown={handleKeyDown}
					className="flex-1 max-w-[300px]"
				/>
				<div className="flex items-center gap-2">
					<Switch
						name={`permanent-new-${listType}`}
						checked={newIsPermanent}
						onChange={() => setNewIsPermanent(!newIsPermanent)}
					/>
					<span className="text-xs text-gray-500">Permanent</span>
				</div>
				<button
					type="button"
					onClick={handleAdd}
					disabled={!newOption.trim()}
					className="inline-flex items-center gap-1.5 rounded-lg bg-[#0080fe] px-4 py-2 text-sm font-medium text-white hover:bg-[#0070e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<Plus className="w-4 h-4" />
					Add
				</button>
			</div>

			{/* Existing options */}
			{items.length === 0 ? (
				<p className="text-sm text-gray-400 py-4 text-center">No options yet. Add one above or seed from existing data.</p>
			) : (
				<div className="divide-y divide-gray-100">
					{items.map(item => (
						<div key={item._id} className="flex items-center gap-3 py-2.5 group">
							<span className="text-sm font-medium text-gray-700 flex-1">{item.option}</span>

							<div className="flex items-center gap-2">
								<Switch
									name={`permanent-${item._id}`}
									checked={!!item.isPermanent}
									onChange={() => handleTogglePermanent(item)}
								/>
								<span className="text-[10px] text-gray-400 w-16">
									{item.isPermanent ? (
										<Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
											Permanent
										</Badge>
									) : (
										'Optional'
									)}
								</span>
							</div>

							{!item.isPermanent ? (
								<button
									type="button"
									onClick={() => handleDelete(item)}
									className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
									title="Delete"
								>
									<Trash2 className="w-3.5 h-3.5" />
								</button>
							) : (
								<div className="w-7" />
							)}
						</div>
					))}
				</div>
			)}
		</div>
	)
}

const LookupListsCN = () => {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const dispatch = useDispatch()

	const { entities, ids, isLoading, isInitialized } = useSelector((state: any) => state.lookupLists)

	useEffect(() => {
		dispatch(fetchLookupLists(undefined) as any)
	}, [dispatch])

	const allItems = useMemo(() => ids.map((id: string) => entities[id]).filter(Boolean), [entities, ids])

	const locationTypes = useMemo(() => allItems.filter((item: any) => item.listType === 'location'), [allItems])
	const projectTypes = useMemo(() => allItems.filter((item: any) => item.listType === 'project'), [allItems])
	const portfolioTypes = useMemo(() => allItems.filter((item: any) => item.listType === 'portfolio'), [allItems])

	const handleSeed = () => {
		dispatch(seedLookupLists() as any).then(() => {
			dispatch(fetchLookupLists(undefined) as any)
		})
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-2xl font-semibold text-gray-800">Lists</h2>
							<button
								type="button"
								onClick={handleSeed}
								className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
							>
								<Database className="w-4 h-4" />
								Seed from existing data
							</button>
						</div>

						<div className="max-w-[800px] mx-auto">
							{isLoading && !isInitialized ? (
								<div className="flex items-center justify-center py-20">
									<Loader2 className="w-6 h-6 animate-spin text-gray-400" />
								</div>
							) : (
								<>
									<LookupListSection
										listType="location"
										title="Location Types"
										items={locationTypes}
										dispatch={dispatch}
									/>
									<LookupListSection
										listType="project"
										title="Project Types"
										items={projectTypes}
										dispatch={dispatch}
									/>
									<LookupListSection
										listType="portfolio"
										title="Portfolio Types"
										items={portfolioTypes}
										dispatch={dispatch}
									/>
								</>
							)}
						</div>
					</main>
				</div>
			</div>
		</div>
	)
}

export default LookupListsCN
